import { Box, HighlightIcon, Icon } from '@dev-spendesk/grapes';
import classnames from 'classnames';
import { type MonetaryValue } from 'ezmoney';

import { CardPayment } from 'src/core/common/components/CardPayment/CardPayment';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { useGoToPayableDetails } from 'src/core/modules/payable';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { type Allocation, type PayableSubType } from '../PayablePanelContainer';

type Props = {
  allocations?: Allocation[];
  creditNoteAmount: MonetaryValue;
  subtype?: PayableSubType;
};

const getPreviews = ({
  allocations,
  goToInvoice,
}: {
  allocations?: Allocation[];
  goToInvoice: (payableId: string) => void;
}) => {
  if (!allocations) {
    return [];
  }

  return allocations.map((allocation, index) => {
    return (
      <div
        key={allocation.expense?.id}
        className={classnames('flex items-center justify-between gap-12', {
          'mb-8': index < allocations.length - 1,
        })}
      >
        <HighlightIcon
          name="invoice"
          size={32}
          variant="purple"
          className="shrink-0"
        />
        <CardPayment
          primarySlot={
            allocation?.amount
              ? formatMonetaryValue({
                  ...allocation.amount,
                  amount: Math.abs(allocation.amount.amount),
                })
              : ''
          }
          secondarySlot={
            allocation.expense?.documentaryEvidence?.invoiceNumber
              ? `#${allocation.expense?.documentaryEvidence?.invoiceNumber}`
              : ''
          }
          tertiarySlot={<Icon name="chevron-right" />}
          onClick={() => {
            goToInvoice(allocation.expense?.id ?? '');
          }}
        />
      </div>
    );
  });
};

const isComponentVisible = (
  allocations?: Allocation[],
  subtype?: PayableSubType,
) => {
  if (
    !allocations ||
    allocations.length === 0 ||
    !allocations.some((allocation) => allocation.entityType === 'expense')
  ) {
    return false;
  }

  return subtype === 'creditNote';
};

const getCreditNoteAllocationsData = (
  creditNoteAmount: MonetaryValue,
  allocations?: Allocation[],
) => {
  if (!allocations) {
    return {
      isFullyAllocated: false,
      remainingAmount: creditNoteAmount,
      numberOfExpensesImpacted: 0,
    };
  }

  const totalAllocatedAmount = allocations.reduce(
    (accumulator, allocation) => accumulator + (allocation.amount?.amount ?? 0),
    0,
  );

  const remainingAmount = Math.abs(
    creditNoteAmount.amount - totalAllocatedAmount,
  );

  return {
    isFullyAllocated: remainingAmount === 0,
    remainingAmount: {
      amount: remainingAmount,
      currency: creditNoteAmount.currency,
      precision: creditNoteAmount.precision,
    },
    numberOfExpensesImpacted: allocations.filter(
      (allocation) => allocation.entityType === 'expense',
    ).length,
  };
};

const getRemainingOnCreditNote = (
  remainingAmount: MonetaryValue,
  t: TGlobalFunctionTyped,
) => {
  const remainingAmountFormatted = formatMonetaryValue(remainingAmount);

  return (
    <div className="pt-16 text-content-secondary-bg-secondary">
      {t('payables.panel.creditNotesCallout.remainingAmount', {
        remainingAmount: remainingAmountFormatted,
      })}
    </div>
  );
};

const getTitle = (
  isFullyAllocated: boolean,
  numberOfExpensesImpacted: number,
  t: TGlobalFunctionTyped,
) => {
  if (!isFullyAllocated) {
    if (numberOfExpensesImpacted === 1) {
      return t('payables.panel.creditNotesCallout.partiallyAllocated.title');
    }
    return t(
      'payables.panel.creditNotesCallout.partiallyAllocated.titlePlural',
    );
  }
  if (numberOfExpensesImpacted === 1) {
    return t('payables.panel.creditNotesCallout.title');
  }
  return t('payables.panel.creditNotesCallout.titlePlural');
};

export const CreditNotesCallout = ({
  allocations,
  subtype,
  creditNoteAmount,
}: Props) => {
  const { t } = useTranslation('global');

  const goToInvoicePayableDetails = useGoToPayableDetails();

  if (!isComponentVisible(allocations, subtype)) {
    return null;
  }

  const { isFullyAllocated, remainingAmount, numberOfExpensesImpacted } =
    getCreditNoteAllocationsData(creditNoteAmount, allocations);

  const title = getTitle(isFullyAllocated, numberOfExpensesImpacted, t);
  return (
    <Box variant="secondary" className="min-h-[80px] items-center">
      <h3 className="mb-16 text-content-primary title-l">{title}</h3>

      {getPreviews({
        allocations,
        goToInvoice: goToInvoicePayableDetails,
      })}

      {!isFullyAllocated && getRemainingOnCreditNote(remainingAmount, t)}
    </Box>
  );
};
